import { GroupData, GroupMode, ResponseItem, SortingRequest, Task } from '@bringg/types';
import { MappedStorableItem } from '@bringg/dashboard-sdk/dist/data-entity';
import { localLogger } from '@bringg-frontend/bringg-web-infra';

import { TableDefinition } from 'bringg-web/features/monitoring/shared/stores/groups/table-definition';
import { groupIdCreator } from './group-id-creator';
import { tasksFilterer } from 'bringg-web/features/monitoring/shared/filters/tasks-filterer';
import { GroupUpdater } from './group-updater';
import { fieldValueGetter } from 'bringg-web/features/monitoring/shared/realtime-updates/updates/field-value-getter';

function getGroupIds(
	{ current, diff }: MappedStorableItem<Task>,
	tableDefinition: TableDefinition
): { newGroupId: string; currentGroupId: string } {
	const groupIdFields = getGroupIdFields(tableDefinition.groupMode);

	const newGroupId =
		current && isInFilter(current, tableDefinition) ? groupIdCreator.create(current, groupIdFields) : undefined;

	const currentGroupId = isInFilter(diff, tableDefinition, current)
		? groupIdCreator.create(diff, groupIdFields, current)
		: undefined;

	return { newGroupId, currentGroupId };
}

function getGroupItems(group: GroupData, tableDefinition: TableDefinition): ResponseItem[] {
	return tableDefinition.itemsByGroup[group?.id];
}

function getSortValues({ current, diff }: MappedStorableItem<Task>, sortingRequest: SortingRequest) {
	const sortingKey = sortingRequest.key === 'teams_ids' ? 'team_ids[0]' : sortingRequest.key;
	const sortValue = fieldValueGetter.get(diff, current, sortingKey);

	const compareValues = [{ field: sortingKey, value: sortValue }];

	if (sortingRequest.key !== 'id') {
		compareValues.push({ field: 'id', value: current ? current.id : diff.id });
	}

	return { values: compareValues, direction: sortingRequest.direction };
}

function getSortFields(tableDefinition: TableDefinition) {
	return [tableDefinition.sorting.key];
}

function addItem(index: number, task: Task, groupId: string, group: GroupData, tableDefinition: TableDefinition) {
	const groupItems = getGroupItems(group, tableDefinition);

	if (group && groupItems) {
		localLogger.log(`realtime update: item - adding to group [${group.id}] in index [${index}]`);
		updateTaskCountInGroup(group, 1, tableDefinition);
		groupItems.splice(index, 0, { id: task.id });
	} else if (groupId) {
		localLogger.log(`realtime update: item - [${task.id}] - adding without group`);

		updateTaskCountInGroup(group, 1, tableDefinition);

		if (!tableDefinition.itemsByGroup[groupId]) {
			tableDefinition.itemsByGroup[groupId] = [];
		}

		tableDefinition.itemsByGroup[groupId].splice(index, 0, { id: task.id });

		const groupParts = groupId.split('-');

		const isFullyLoaded =
			groupParts.length === 1
				? !tableDefinition.hasMoreData
				: tableDefinition.isGroupFullyLoaded(tableDefinition.groupsMap[groupParts[0]]);

		if (isFullyLoaded && groupParts[groupParts.length - 1] === 'null') {
			const container = getContainer(groupParts, tableDefinition);

			if (container && tableDefinition.entityBaseType !== 'task_group') {
				localLogger.log(`realtime update: item - [${task.id}] - creating null group`);
				tableDefinition.addGroup(container, {
					groupId,
					groupType: container[0].group_type,
					data: { id: null, title: null, task_count: tableDefinition.itemsByGroup[groupId].length },
					index: container.length
				});
			}
		}
	}
}

function removeItem(group: GroupData, index: number, tableDefinition: TableDefinition) {
	const groupItems = getGroupItems(group, tableDefinition);
	groupItems.splice(index, 1);
	updateTaskCountInGroup(group, -1, tableDefinition);

	if (tableDefinition.itemsByGroup[group?.id].length === 0) {
		const groupParts = group.id.split('-');
		const container = getContainer(groupParts, tableDefinition);

		if (container) {
			const groupIndex = container.findIndex(currentGroup => currentGroup.id === group.id);

			localLogger.log(
				`realtime update: removing empty group [${group.id || group['group_id']}] from index ${groupIndex}`
			);

			if (groupIndex > -1) {
				tableDefinition.removeGroup(container, groupIndex);
			}
		}
	}
}

function getContainer(groupParts: string[], tableDefinition: TableDefinition) {
	return groupParts.length === 1 ? tableDefinition.groups : tableDefinition.groupsMap[groupParts[0]]?.groups;
}

function getGroupIdFields(groupMode: GroupMode) {
	switch (groupMode) {
		case GroupMode.None:
			return [];
		case GroupMode.Team:
			return ['team_ids[0]'];
		case GroupMode.TeamRoute:
			return ['team_ids[0]', 'run_id'];
		case GroupMode.Route:
			return ['run_id'];
		case GroupMode.TeamDriver:
			return ['team_ids[0]', 'user_id'];
		case GroupMode.Driver:
			return ['user_id'];
		case GroupMode.FleetTeam:
			return ['team_ids[0]', 'fleet_id'];
		case GroupMode.Fleet:
			return ['fleet_id'];
		default:
			throw new Error(`unsupported group mode type ${groupMode}`);
	}
}

function isInFilter(task: Task, currentTableDefinition: TableDefinition, fallback?: Task) {
	return (
		tasksFilterer.filter(currentTableDefinition.filters.task, task, fallback) &&
		tasksFilterer.filter(currentTableDefinition.filters.run, task, fallback)
	);
}

function updateTaskCountInGroup(group: GroupData, by: number, tableDefinition: TableDefinition) {
	const logPrefix = `realtime update: group -`;

	tableDefinition.totalTasks += by;

	localLogger.log(
		`${logPrefix} [${group?.id}] - updating total tasks by ${by} - new total ${tableDefinition.totalTasks}`
	);

	if (!group) {
		return;
	}

	if (group.data?.task_count !== undefined) {
		group.data.task_count += by;
		localLogger.log(
			`${logPrefix} [${group?.id}] - updating group tasks count by ${by} - new total ${group.data.task_count}`
		);
	}

	const parts = group.id.split('-');
	const parentGroup = parts.length > 1 ? tableDefinition.groupsMap[parts[0]] : undefined;
	if (parentGroup && parentGroup.data?.task_count !== undefined) {
		parentGroup.data.task_count += by;
		localLogger.log(
			`${logPrefix} parent [${parentGroup?.id}] - updating group tasks count by ${by} - new total ${parentGroup.data.task_count}`
		);
	}
}

export const tasksGroupUpdater: GroupUpdater<Task, ResponseItem> = {
	getGroupIds,
	getGroupItems,
	getSortValues,
	getSortFields,
	addItem,
	removeItem
};
